import { mapActions, mapGetters } from 'vuex'
import KPI from '@/components/KPI/KPI.vue'
import { isGoogleAnalyticsConfigured } from '@/views/Director/director.helpers'

export default {
  name: 'DirectorGoogleAnalyticsView',
  props: {
    contentplanId: {
      type: Number,
      required: true
    },
    workspaceId: {
      type: Number,
      required: true
    },
    contentplanType: {
      type: String,
      default: 'managed'
    },
    datesFilter: {
      type: Array,
      required: true
    },
    sharingKey: {
      type: String,
      default: ''
    },
    isSharedView: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      totalSiteMetrics: {
        sessions: 0,
        revenue: 0,
        transactions: 0,
        loading: false
      },
      contentGeckoMetrics: {
        sessions: 0,
        revenue: 0,
        transactions: 0,
        loading: false
      },
      topPages: {
        items: [],
        loading: false,
        error: ''
      },
      currency: 'EUR'
    }
  },
  computed: {
    ...mapGetters([
      'integrations',
      'user'
    ]),
    analyticsIntegration () {
      return this.integrations?.find(item => item.type === 'ga-4') || {}
    },
    gaConnected () {
      return isGoogleAnalyticsConfigured(this.analyticsIntegration)
    },
    isEcommerceType () {
      return this.contentplanType === 'ecommerce'
    },
    googleAnalyticsEvents () {
      const events = this.analyticsIntegration?.ga_events
      if (!events) return []
      return events.split(',').map(event => event.trim()).filter(event => event.length > 0)
    },
    topPagesData () {
      if (!Array.isArray(this.topPages.items)) return []
      return this.topPages.items
    },
    totalSiteTraffic () {
      return this.totalSiteMetrics.sessions ?? 0
    },
    contentGeckoTraffic () {
      return this.contentGeckoMetrics.sessions ?? 0
    },
    totalSitePrimaryLabel () {
      if (this.isEcommerceType) return `Total Site Revenue (${this.currency})`
      return 'Total Site Conversions'
    },
    contentGeckoPrimaryLabel () {
      if (this.isEcommerceType) return `ContentGecko Revenue (${this.currency})`
      return 'ContentGecko Conversions'
    },
    totalSitePrimaryValue () {
      if (this.isEcommerceType) return this.totalSiteMetrics.revenue ?? 0
      return this.totalSiteMetrics.transactions ?? 0
    },
    contentGeckoPrimaryValue () {
      if (this.isEcommerceType) return this.contentGeckoMetrics.revenue ?? 0
      return this.contentGeckoMetrics.transactions ?? 0
    },
    summaryText () {
      if (this.isEcommerceType) return 'This report estimates sales impact from ContentGecko pages using Google Analytics data.'
      return 'This report estimates conversion impact from ContentGecko pages using Google Analytics data.'
    },
    topPagesTitle () {
      if (this.isEcommerceType) return 'Top ContentGecko Pages by Sales Activity'
      return 'Top ContentGecko Pages by Conversions'
    },
    topPagesHeaders () {
      if (this.isEcommerceType) {
        return [
          { text: 'Page URL', value: 'url', align: 'start' },
          { text: 'Sessions', value: 'sessions', align: 'end' },
          { text: 'Product Views', value: 'productViews', align: 'end' },
          { text: 'Add to Carts', value: 'addToCarts', align: 'end' },
          { text: 'Initiate Checkout', value: 'initiateCheckout', align: 'end' },
          { text: 'Total Revenue', value: 'revenue', align: 'end' },
          { text: 'Transactions', value: 'transactions', align: 'end' },
          { text: 'Revenue/Session', value: 'revenuePerSession', align: 'end' }
        ]
      }

      return [
        { text: 'Page URL', value: 'url', align: 'start' },
        { text: 'Sessions', value: 'sessions', align: 'end' },
        { text: 'Transactions', value: 'transactions', align: 'end' },
        { text: 'Conversion Rate', value: 'conversionRate', align: 'end' }
      ]
    }
  },
  watch: {
    datesFilter: {
      deep: true,
      handler () {
        this.loadGoogleAnalyticsData()
      }
    }
  },
  methods: {
    ...mapActions([
      'getGoogleAnalyticsData'
    ]),
    hasValidDateRange () {
      return Array.isArray(this.datesFilter) && this.datesFilter.length === 2 && this.datesFilter[0] && this.datesFilter[1]
    },
    resetGoogleAnalyticsData () {
      this.totalSiteMetrics.sessions = 0
      this.totalSiteMetrics.revenue = 0
      this.totalSiteMetrics.transactions = 0
      this.contentGeckoMetrics.sessions = 0
      this.contentGeckoMetrics.revenue = 0
      this.contentGeckoMetrics.transactions = 0
      this.topPages.items = []
      this.topPages.error = ''
      this.currency = 'EUR'
    },
    formatCurrency (value) {
      const num = parseFloat(value) || 0
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: this.currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(num)
    },
    formatPercent (value) {
      const num = parseFloat(value) || 0
      return `${num.toFixed(2)}%`
    },
    async loadTotalSiteMetrics () {
      try {
        this.totalSiteMetrics.loading = true
        const payload = {
          startDate: this.datesFilter[0],
          endDate: this.datesFilter[1],
          contentplanId: this.contentplanId,
          dimensions: [],
          metrics: [
            { name: 'sessions' },
            { name: 'totalRevenue' },
            { name: 'transactions' }
          ],
          filters: [],
          calculation: 'sum',
          calculationOptions: {
            fields: [
              { field: 'sessions', as: 'sessions', type: 'int' },
              { field: 'totalRevenue', as: 'totalRevenue', type: 'float' },
              { field: 'transactions', as: 'transactions', type: 'int' }
            ]
          }
        }

        if (this.sharingKey) payload.key = this.sharingKey

        const response = await this.getGoogleAnalyticsData(payload)
        this.totalSiteMetrics.sessions = response?.totals?.sessions ?? 0
        this.totalSiteMetrics.revenue = response?.totals?.totalRevenue ?? 0
        this.totalSiteMetrics.transactions = response?.totals?.transactions ?? 0
      } catch (error) {
        console.error('Failed to fetch total site metrics', error)
      } finally {
        this.totalSiteMetrics.loading = false
      }
    },
    async loadContentGeckoMetrics () {
      try {
        this.contentGeckoMetrics.loading = true
        const payload = {
          startDate: this.datesFilter[0],
          endDate: this.datesFilter[1],
          contentplanId: this.contentplanId,
          dimensions: [],
          metrics: [
            { name: 'sessions' },
            { name: 'totalRevenue' },
            { name: 'transactions' }
          ],
          filters: [],
          onlyContentGeckoPages: true,
          calculation: 'sum',
          calculationOptions: {
            fields: [
              { field: 'sessions', as: 'sessions', type: 'int' },
              { field: 'totalRevenue', as: 'totalRevenue', type: 'float' },
              { field: 'transactions', as: 'transactions', type: 'int' }
            ]
          }
        }

        if (this.sharingKey) payload.key = this.sharingKey

        const response = await this.getGoogleAnalyticsData(payload)
        this.contentGeckoMetrics.sessions = response?.totals?.sessions ?? 0
        this.contentGeckoMetrics.revenue = response?.totals?.totalRevenue ?? 0
        this.contentGeckoMetrics.transactions = response?.totals?.transactions ?? 0
      } catch (error) {
        console.error('Failed to fetch ContentGecko metrics', error)
      } finally {
        this.contentGeckoMetrics.loading = false
      }
    },
    async loadTopPages () {
      try {
        this.topPages.loading = true
        this.topPages.error = ''

        const eventValues = this.googleAnalyticsEvents.length > 0 ? this.googleAnalyticsEvents : ['view_item', 'add_to_cart', 'begin_checkout']
        const payload = {
          startDate: this.datesFilter[0],
          endDate: this.datesFilter[1],
          contentplanId: this.contentplanId,
          dimensions: [{ name: 'landingPagePlusQueryString' }],
          metrics: [
            { name: 'sessions' },
            { name: 'totalRevenue' },
            { name: 'transactions' }
          ],
          filters: [],
          onlyContentGeckoPages: true,
          calculation: 'revenueSummary',
          calculationOptions: {
            eventSourceId: 'events',
            eventMappings: {
              view_item: 'productViews',
              add_to_cart: 'addToCarts',
              begin_checkout: 'initiateCheckout'
            },
            limit: 200,
            currencyFallback: 'EUR'
          },
          additionalReports: [
            {
              id: 'events',
              dimensions: [{ name: 'landingPagePlusQueryString' }, { name: 'eventName' }],
              metrics: [{ name: 'eventCount' }],
              filters: [{
                filter: {
                  fieldName: 'eventName',
                  inListFilter: {
                    values: eventValues
                  }
                }
              }],
              onlyContentGeckoPages: true
            }
          ]
        }

        if (this.sharingKey) payload.key = this.sharingKey

        const response = await this.getGoogleAnalyticsData(payload)
        const items = (response?.items ?? []).map(item => {
          const sessions = parseInt(item.sessions, 10) || 0
          const transactions = parseInt(item.transactions, 10) || 0
          const conversionRate = sessions > 0 ? (transactions / sessions) * 100 : 0
          return {
            ...item,
            sessions,
            transactions,
            conversionRate
          }
        })

        const sortedItems = this.isEcommerceType
          ? items.sort((a, b) => (b.productViews || 0) - (a.productViews || 0))
          : items.sort((a, b) => (b.transactions || 0) - (a.transactions || 0))

        this.topPages.items = sortedItems
        this.currency = response?.currency ?? 'EUR'
      } catch (error) {
        console.error('Failed to fetch top pages', error)
        this.topPages.error = error?.response?.data?.message || error.message || 'Failed to load top pages.'
        this.currency = 'EUR'
      } finally {
        this.topPages.loading = false
      }
    },
    async loadGoogleAnalyticsData () {
      if (!this.hasValidDateRange()) return
      if (!this.gaConnected || (!this.user && !this.sharingKey)) {
        this.resetGoogleAnalyticsData()
        return
      }

      await Promise.all([
        this.loadTotalSiteMetrics(),
        this.loadContentGeckoMetrics(),
        this.loadTopPages()
      ])
    }
  },
  async mounted () {
    await this.loadGoogleAnalyticsData()
  },
  components: { KPI }
}
